import { useSyncExternalStore } from "react";
import { moneySign, roundMoney } from "@fomofi/shared";
import type { CurrencyDisplay } from "@/api/profile";

/**
 * App-wide currency formatting that honors the user's `currency_display`
 * preference. Only "symbol" ($1,234) remains: "code" (USD 1,234) was retired in
 * BEN-141, so the store is kept for its subscribers but never changes.
 *
 * Backed by a tiny external store so a single source of truth drives every
 * `formatCurrency()` call across the app. `setCurrencyDisplay` is called once
 * when the profile loads and again when the preference is saved in Settings;
 * `<CurrencyProvider>` subscribes via `useCurrencyDisplay()` and re-renders the
 * app subtree so all (non-memoized) money displays reformat immediately.
 */

let _display: CurrencyDisplay = "symbol";
const _listeners = new Set<() => void>();

export function setCurrencyDisplay(display: CurrencyDisplay): void {
  if (display === _display) return;
  _display = display;
  _listeners.forEach((l) => l());
}

export function getCurrencyDisplay(): CurrencyDisplay {
  return _display;
}

function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Reactive read of the current currency display preference. */
export function useCurrencyDisplay(): CurrencyDisplay {
  return useSyncExternalStore(subscribe, getCurrencyDisplay, getCurrencyDisplay);
}

/**
 * Format a USD amount honoring the active currency-display preference.
 * `withCents` keeps the previous per-call behavior (whole dollars by default,
 * cents when explicitly requested) so this is a drop-in for the formatters
 * previously inlined across the app.
 */
export function formatCurrency(amount: number, withCents = false): string {
  const digits = withCents ? 2 : 0;
  // roundMoney first: a figure that rounds to zero is +0, and Intl prints −0 as "-$0"
  // (CALL-06).
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "symbol",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(roundMoney(amount, digits));
}

/**
 * Money with its direction as a sign: "+$12", "-$12" ("−$12" with `minus: "−"`). The
 * sign comes from `amount`, and a figure that shows as zero gets none: "$0" / "$0.00",
 * so a period with no expenses never reads "−$0" (CALL-06). `plus: ""` signs only
 * negatives. A line that shows a positive magnitude as an outflow passes it negated.
 */
export function formatSignedCurrency(
  amount: number,
  {
    cents = false,
    plus = "+",
    minus = "-",
  }: { cents?: boolean; plus?: string; minus?: string } = {},
): string {
  const sign = moneySign(amount, cents ? 2 : 0, { plus, minus });
  return `${sign}${formatCurrency(Math.abs(amount), cents)}`;
}
